import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Save, Eye, Settings, Palette, Shield, Bell, Globe } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const CustomerPortal = () => {
  const [portalSettings, setPortalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const portalStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'draft', label: 'Draft' }
  ];

  useEffect(() => {
    fetchPortalSettings();
  }, []);

  const fetchPortalSettings = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user's organization
      const { data: orgMember } = await supabase?.from('organization_members')?.select('organization_id')?.eq('user_id', user?.id)?.single();

      if (!orgMember) throw new Error('User not associated with any organization');

      const { data, error } = await supabase?.from('customer_portal_settings')?.select('*')?.eq('organization_id', orgMember?.organization_id)?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      if (data) {
        setPortalSettings(data);
      } else {
        // Create default settings if none exist
        const defaultSettings = {
          organization_id: orgMember?.organization_id,
          status: 'draft',
          theme_settings: {
            primaryColor: '#3b82f6',
            secondaryColor: '#64748b',
            logo: null,
            customCss: ''
          },
          features_enabled: {
            invoiceDownload: true,
            paymentHistory: true,
            profileManagement: true,
            supportTickets: false,
            documentAccess: true
          },
          notification_settings: {
            emailNotifications: true,
            paymentReminders: true,
            invoiceAlerts: true
          },
          access_restrictions: {
            allowedIpAddresses: [],
            requireTwoFactor: false,
            sessionTimeout: 30
          },
          welcome_message: 'Welcome to our customer portal! Access your invoices and account information here.',
          footer_text: 'Contact our support team for assistance.'
        };

        const { data: newData, error: insertError } = await supabase?.from('customer_portal_settings')?.insert([defaultSettings])?.select()?.single();

        if (insertError) throw insertError;
        setPortalSettings(newData);
      }
    } catch (error) {
      console.error('Error fetching portal settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!portalSettings) return;

    try {
      setSaving(true);
      const { error } = await supabase?.from('customer_portal_settings')?.update(portalSettings)?.eq('id', portalSettings?.id);

      if (error) throw error;
      
      alert('Customer portal settings saved successfully!');
    } catch (error) {
      console.error('Error saving portal settings:', error);
      alert(`Error saving settings: ${error?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (path, value) => {
    setPortalSettings(prev => {
      const newSettings = { ...prev };
      const keys = path?.split('.');
      let current = newSettings;
      
      for (let i = 0; i < keys?.length - 1; i++) {
        if (!current?.[keys?.[i]]) current[keys[i]] = {};
        current = current?.[keys?.[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Portal Status
          </label>
          <Select
            value={portalSettings?.status || 'draft'}
            onChange={(e) => updateSettings('status', e?.target?.value)}
            options={portalStatusOptions}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Set to Active to make the portal available to customers
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Custom Domain
          </label>
          <Input
            type="text"
            value={portalSettings?.custom_domain || ''}
            onChange={(e) => updateSettings('custom_domain', e?.target?.value)}
            placeholder="portal.yourcompany.com"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Optional: Use your own domain for the portal
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Welcome Message
        </label>
        <textarea
          value={portalSettings?.welcome_message || ''}
          onChange={(e) => updateSettings('welcome_message', e?.target?.value)}
          placeholder="Enter a welcome message for your customers"
          rows={3}
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Footer Text
        </label>
        <Input
          type="text"
          value={portalSettings?.footer_text || ''}
          onChange={(e) => updateSettings('footer_text', e?.target?.value)}
          placeholder="Contact information or footer text"
        />
      </div>
    </div>
  );

  const renderFeaturesTab = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-foreground mb-4">Available Features</h4>
        <div className="space-y-4">
          {[
            { key: 'invoiceDownload', label: 'Invoice Download', description: 'Allow customers to download their invoices' },
            { key: 'paymentHistory', label: 'Payment History', description: 'Show payment history and status' },
            { key: 'profileManagement', label: 'Profile Management', description: 'Allow customers to update their profile' },
            { key: 'supportTickets', label: 'Support Tickets', description: 'Enable support ticket creation and tracking' },
            { key: 'documentAccess', label: 'Document Access', description: 'Access to contracts and important documents' }
          ]?.map((feature) => (
            <div key={feature?.key} className="flex items-start space-x-3 p-4 bg-surface rounded-lg border border-border">
              <Checkbox
                checked={portalSettings?.features_enabled?.[feature?.key] || false}
                onChange={(checked) => updateSettings(`features_enabled.${feature?.key}`, checked)}
              />
              <div>
                <label className="text-sm font-medium text-foreground">{feature?.label}</label>
                <p className="text-xs text-muted-foreground">{feature?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderThemeTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Primary Color
          </label>
          <div className="flex items-center space-x-2">
            <Input
              type="color"
              value={portalSettings?.theme_settings?.primaryColor || '#3b82f6'}
              onChange={(e) => updateSettings('theme_settings.primaryColor', e?.target?.value)}
              className="w-16 h-10"
            />
            <Input
              type="text"
              value={portalSettings?.theme_settings?.primaryColor || '#3b82f6'}
              onChange={(e) => updateSettings('theme_settings.primaryColor', e?.target?.value)}
              placeholder="#3b82f6"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Secondary Color
          </label>
          <div className="flex items-center space-x-2">
            <Input
              type="color"
              value={portalSettings?.theme_settings?.secondaryColor || '#64748b'}
              onChange={(e) => updateSettings('theme_settings.secondaryColor', e?.target?.value)}
              className="w-16 h-10"
            />
            <Input
              type="text"
              value={portalSettings?.theme_settings?.secondaryColor || '#64748b'}
              onChange={(e) => updateSettings('theme_settings.secondaryColor', e?.target?.value)}
              placeholder="#64748b"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Logo URL
        </label>
        <Input
          type="text"
          value={portalSettings?.theme_settings?.logo || ''}
          onChange={(e) => updateSettings('theme_settings.logo', e?.target?.value)}
          placeholder="https://example.com/logo.png"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Custom CSS
        </label>
        <textarea
          value={portalSettings?.theme_settings?.customCss || ''}
          onChange={(e) => updateSettings('theme_settings.customCss', e?.target?.value)}
          placeholder="Enter custom CSS for advanced styling"
          rows={6}
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
        />
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-foreground mb-4">Notification Settings</h4>
        <div className="space-y-4">
          {[
            { key: 'emailNotifications', label: 'Email Notifications', description: 'Send general email notifications to customers' },
            { key: 'paymentReminders', label: 'Payment Reminders', description: 'Send automatic payment reminder emails' },
            { key: 'invoiceAlerts', label: 'Invoice Alerts', description: 'Notify customers when new invoices are available' }
          ]?.map((setting) => (
            <div key={setting?.key} className="flex items-start space-x-3 p-4 bg-surface rounded-lg border border-border">
              <Checkbox
                checked={portalSettings?.notification_settings?.[setting?.key] || false}
                onChange={(checked) => updateSettings(`notification_settings.${setting?.key}`, checked)}
              />
              <div>
                <label className="text-sm font-medium text-foreground">{setting?.label}</label>
                <p className="text-xs text-muted-foreground">{setting?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Session Timeout (minutes)
        </label>
        <Input
          type="number"
          value={portalSettings?.access_restrictions?.sessionTimeout || 30}
          onChange={(e) => updateSettings('access_restrictions.sessionTimeout', parseInt(e?.target?.value))}
          min="5"
          max="480"
        />
        <p className="text-xs text-muted-foreground mt-1">
          How long customers can stay logged in without activity
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-start space-x-3 p-4 bg-surface rounded-lg border border-border">
          <Checkbox
            checked={portalSettings?.access_restrictions?.requireTwoFactor || false}
            onChange={(checked) => updateSettings('access_restrictions.requireTwoFactor', checked)}
          />
          <div>
            <label className="text-sm font-medium text-foreground">Require Two-Factor Authentication</label>
            <p className="text-xs text-muted-foreground">Force customers to use 2FA for enhanced security</p>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Allowed IP Addresses
        </label>
        <textarea
          value={portalSettings?.access_restrictions?.allowedIpAddresses?.join('\n') || ''}
          onChange={(e) => updateSettings('access_restrictions.allowedIpAddresses', e?.target?.value?.split('\n')?.filter(ip => ip?.trim()))}
          placeholder="192.168.1.100&#10;203.0.113.0/24&#10;Leave empty to allow all IPs"
          rows={4}
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
        <p className="text-xs text-muted-foreground mt-1">
          One IP address or CIDR block per line. Leave empty to allow all IPs.
        </p>
      </div>
    </div>
  );

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'features', label: 'Features', icon: Globe },
    { id: 'theme', label: 'Theme', icon: Palette },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Customer Portal</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure your customer-facing portal for invoice access and account management
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Preview Portal
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
      {/* Status Badge */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Status:</span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          portalSettings?.status === 'active' ?'bg-green-100 text-green-800' 
            : portalSettings?.status === 'inactive' ?'bg-red-100 text-red-800' :'bg-yellow-100 text-yellow-800'
        }`}>
          {portalSettings?.status?.charAt(0)?.toUpperCase() + portalSettings?.status?.slice(1) || 'Draft'}
        </span>
      </div>
      {/* Tabs */}
      <div className="bg-surface rounded-lg border border-border">
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs?.map((tab) => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab?.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'features' && renderFeaturesTab()}
          {activeTab === 'theme' && renderThemeTab()}
          {activeTab === 'notifications' && renderNotificationsTab()}
          {activeTab === 'security' && renderSecurityTab()}
        </div>
      </div>
    </div>
  );
};

export default CustomerPortal;